/**
 * Light / dark / follow-the-OS, stamped onto `<html data-theme>`.
 *
 * Dark mode is a selected palette rather than an inverted one - the tokens in
 * `index.css` are re-stepped for the dark surface - so all this has to do is decide
 * which set is active. The `system` choice keeps listening, so an operator whose OS
 * flips at sunset follows without reloading.
 */

import { useCallback, useEffect, useState } from 'react'

export type ThemeChoice = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'whd-theme'

function readStored(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    /* Private mode can throw; following the OS is a fine default. */
  }
  return 'system'
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(readStored)
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    readStored() === 'system' ? systemTheme() : (readStored() as ResolvedTheme),
  )

  useEffect(() => {
    const next = choice === 'system' ? systemTheme() : choice
    setResolved(next)
    document.documentElement.setAttribute('data-theme', next)
    try {
      if (choice === 'system') localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, choice)
    } catch {
      /* Not being able to remember the choice is not worth failing over. */
    }
  }, [choice])

  useEffect(() => {
    if (choice !== 'system') return
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      const next = systemTheme()
      setResolved(next)
      document.documentElement.setAttribute('data-theme', next)
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [choice])

  const toggle = useCallback(() => {
    setChoice(resolved === 'dark' ? 'light' : 'dark')
  }, [resolved])

  return { choice, resolved, setChoice, toggle }
}
